import React, { useEffect, useMemo, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { FontAwesome5, MaterialIcons } from '@expo/vector-icons';

import { getAddressesData, deleteAddress, getStreets, getPlantations, getPropertyNames, updateCoordinates } from '../../../api/AllAddress ';
import { BasicLayout, CommonContainer } from '../../../common/components/CustomLayout';
import { BOHTBody, BOHTD, BOHTDIconBox, BOHTH, BOHTH2, BOHTHead, BOHTR, BOHTable } from '../../../common/components/bohtable';
import { BOHButton, BOHTlbrSearchInput, BOHTlbrSearchPicker, BOHToolbar } from '../../../common/components/bohtoolbar';
import { msgStr } from '../../../common/constants/Message';
import { TextMediumSize } from '../../../common/constants/Fonts';
import { useAlertModal, useConfirmModal } from '../../../common/hooks';

import AddLocationModal from './AddLocationModal';
import { useSorting } from '../../../common/hooks/useSorting';

const LocationManager = ({ navigation }) => {
  const { showAlert } = useAlertModal();
  const { showConfirm } = useConfirmModal();

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [updateLocationTrigger, setUpdateLocationsTrigger] = useState(true);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const InitialWidths = [40, 80, 170, 170, 170, 90, 100, 100, 100, 200, 90, 90, 50, 50];
  const [streets, setStreets] = useState([]);
  const [plantataions, setPlantations] = useState([]);
  const [propertyNames, setPropertyNames] = useState([]);
  const [searchOptions, setSearchOptions] = useState({
    searchKey: '',
    street: '',
    plantation: '',
    property_name: '',
  })

  const [isAddModalVisible, setAddModalVisible] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const openAddLocationModal = () => {
    setAddModalVisible(true);
    setSelectedLocation(null);
  };
  const closeAddLocationModal = () => {
    setAddModalVisible(false);
    setSelectedLocation(null);
  };
  const editLocation = (item) => {
    setSelectedLocation(item);
    setAddModalVisible(true);
  };

  const changeSearchOptions = (key, val) => {
    setSearchOptions(prevOptions => ({
      ...prevOptions,
      [key]: val
    }));
  }

  useEffect(() => {
    getStreets((jsonRes) => {
      setStreets(jsonRes);
    });
    getPlantations((jsonRes) => {
      setPlantations(jsonRes);
    });
    getPropertyNames((jsonRes) => {
      setPropertyNames(jsonRes);
    });
  }, [])

  useEffect(() => {
    if (updateLocationTrigger == true) getTable();
  }, [updateLocationTrigger]);

  const removeLocation = (id) => {
    showConfirm(msgStr('deleteConfirmStr'), () => {
      deleteAddress(id, (jsonRes, status, error) => {
        switch (status) {
          case 200:
            setUpdateLocationsTrigger(true);
            showAlert('success', jsonRes.message);
            break;
          default:
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
            break;
        }
      });
    });
  };

  const updateLocationsCoordinates = () => {
    showConfirm('Are you sure you want to update the latitude and longitude for the selected locations?', () => {
      setIsLoading(true);
      updateCoordinates({ ids: selectedLocations }, (jsonRes, status, error) => {
        setIsLoading(false);
        switch (status) {
          case 200:
            setUpdateLocationsTrigger(true);
            showAlert('success', jsonRes.message || 'Locations updated successfully');
            setSelectedLocations([]);
            break;
          default:
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
            break;
        }
      });
    });
  };

  useEffect(() => {
    setUpdateLocationsTrigger(true);
  }, [searchOptions])

  const getTable = () => {
    setIsLoading(true);
    getAddressesData(searchOptions, (jsonRes, status, error) => {
      switch (status) {
        case 200:
          setUpdateLocationsTrigger(false);
          setTableData(jsonRes);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };
  const { sortedData, handleSort, sorting } = useSorting('locations', tableData, { key: 'number', direction: 'ascending' });

  // Handle checkbox selection for a single item
  const handleSelectLocation = (id) => {
    setSelectedLocations(prevSelected => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter(itemId => itemId !== id);
      } else {
        return [...prevSelected, id];
      }
    });
  };

  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectedLocations.length === sortedData.length) {
      // If all are selected, unselect all
      setSelectedLocations([]);
    } else {
      // Otherwise select all
      setSelectedLocations(sortedData.map(item => item.id));
    }
  };

  const renderTableData = useMemo(() => {
    const rows = [];
    if (sortedData.length > 0) {
      sortedData.map((item, index) => {
        const isSelected = selectedLocations.includes(item.id);
        rows.push(
          <BOHTR key={index}>
            <BOHTDIconBox width={InitialWidths[0]}>
              <TouchableOpacity
                onPress={() => handleSelectLocation(item.id)}
              >
                <MaterialIcons 
                  size={TextMediumSize} 
                  name={isSelected ? "check-box" : "check-box-outline-blank"} 
                  color={isSelected ? "#007AFF" : "#777"} 
                />
              </TouchableOpacity>
            </BOHTDIconBox>
            <BOHTD width={InitialWidths[1]}>{item.number}</BOHTD>
            <BOHTD width={InitialWidths[2]}>{item.street}</BOHTD>
            <BOHTD width={InitialWidths[3]}>{item.plantation}</BOHTD>
            <BOHTD width={InitialWidths[4]}>{item.property_name}</BOHTD>
            <BOHTD width={InitialWidths[5]}>{item.property_type == 0 ? 'House' : item.property_type == 1 ? 'Condo' : ''}</BOHTD>
            <BOHTD width={InitialWidths[6]}>{item.rental_company}</BOHTD>
            <BOHTD width={InitialWidths[7]} textAlign='right'>{item.voucher_potential}</BOHTD>
            <BOHTD width={InitialWidths[8]} textAlign='right'>{item.fif_potential}</BOHTD>
            <BOHTD width={InitialWidths[9]}>{item.address}</BOHTD>
            <BOHTD width={InitialWidths[10]}>{item.geolat}</BOHTD>
            <BOHTD width={InitialWidths[11]}>{item.geolong}</BOHTD>
            <BOHTDIconBox width={InitialWidths[12]}>
              <TouchableOpacity
                onPress={() => {
                  editLocation(item);
                }}
              >
                <FontAwesome5 size={TextMediumSize} name="edit" color="black" />
              </TouchableOpacity>
            </BOHTDIconBox>
            <BOHTDIconBox width={InitialWidths[13]}>
              <TouchableOpacity
                onPress={() => {
                  removeLocation(item.id);
                }}
              >
                <FontAwesome5 size={TextMediumSize} name="times" color="black" />
              </TouchableOpacity>
            </BOHTDIconBox>
          </BOHTR>
        );
      });
    } else {
      <></>;
    }

    setIsLoading(false);
    return <>{rows}</>;
  }, [sortedData, selectedLocations]);
  const headers = [
    { key: 'number', label: 'Number' },
    { key: 'street', label: 'Street' },
    { key: 'plantation', label: 'Area' },
    { key: 'property_name', label: 'Property Name' },
    { key: 'property_type', label: 'Property Type' },
    { key: 'rental_company', label: 'Rental Company' },
    { key: 'voucher_potential', label: 'Voucher Potential' },
    { key: 'fif_potential', label: 'FIF Potential' },
    { key: 'address', label: 'Address' },
    { key: 'geolat', label: 'Latitude' },
    { key: 'geolong', label: 'Longitude' },
  ];
  const tableElement = useMemo(() => (
    <BOHTable isLoading={isLoading}>
      <BOHTHead>
        <BOHTR>
          <BOHTDIconBox width={InitialWidths[0]}>
            <TouchableOpacity onPress={handleSelectAll}>
              <MaterialIcons 
                size={TextMediumSize} 
                name={selectedLocations.length === sortedData.length && sortedData.length > 0 ? "check-box" : "check-box-outline-blank"} 
                color={selectedLocations.length > 0 ? "#007AFF" : "#777"} 
              />
            </TouchableOpacity>
          </BOHTDIconBox>
          {headers.map(({ key, label }, wIndex) => (
            <BOHTH
              key={key}
              width={InitialWidths[wIndex + 1]} // +1 to account for checkbox column
              isSorted={sorting.key === key}
              direction={sorting.key === key ? sorting.direction : null}
              onClick={() => handleSort(key)}
            >
              {label}
            </BOHTH>
          ))}
          <BOHTH2 width={InitialWidths[10]}>{'Edit'}</BOHTH2>
          <BOHTH2 width={InitialWidths[11]}>{'DEL'}</BOHTH2>
        </BOHTR>
      </BOHTHead>
      <BOHTBody>
        {renderTableData}
      </BOHTBody>
    </BOHTable>), [isLoading, sortedData, selectedLocations])

  return (
    <BasicLayout
      navigation={navigation}
      goBack={() => {
        navigation.navigate('Marketing');
      }}
      screenName={'Location Manager'}
    >
      <CommonContainer>
        <BOHToolbar>
          <BOHButton
            label="Add"
            onPress={openAddLocationModal} />
          {selectedLocations.length > 0 && (
            <BOHButton
              style={{ marginLeft: 8 }}
              label={`Update Coordinates (${selectedLocations.length})`}
              onPress={updateLocationsCoordinates}
            />
          )}
          <BOHTlbrSearchInput
            label="Search"
            value={searchOptions.searchKey}
            onChangeText={(val) => changeSearchOptions('searchKey', val)} />
          <BOHTlbrSearchPicker
            width={136}
            label="Area"
            items={[
              { label: '', value: '' },
              ...plantataions.map((item, index) => {
                if (item && item.trim()) {
                  return { label: item.trim(), value: item.trim() };
                } else {
                  return null;
                }
              })
                .filter(item => item !== null)
            ]}
            selectedValue={searchOptions.plantation || ''}
            onValueChange={val => changeSearchOptions('plantation', val)}
          />
          <BOHTlbrSearchPicker
            width={136}
            label="Street"
            items={[
              { label: '', value: '' },
              ...streets.map((item, index) => {
                if (item && item.trim()) {
                  return { label: item.trim(), value: item.trim() };
                } else {
                  return null;
                }
              })
                .filter(item => item !== null)
            ]}
            selectedValue={searchOptions.street || ''}
            onValueChange={val => changeSearchOptions('street', val)}
          />
          <BOHTlbrSearchPicker
            width={136}
            label="Property Name"
            items={[
              { label: '', value: '' },
              ...propertyNames.map((item, index) => {
                if (item && item.trim()) {
                  return { label: item.trim(), value: item.trim() };
                } else {
                  return null;
                }
              })
                .filter(item => item !== null)
            ]}
            selectedValue={searchOptions.property_name || ''}
            onValueChange={val => changeSearchOptions('property_name', val)}
          />
        </BOHToolbar>
        {tableElement}
        <AddLocationModal
          isModalVisible={isAddModalVisible}
          details={selectedLocation}
          setUpdateLocationsTrigger={setUpdateLocationsTrigger}
          closeModal={closeAddLocationModal}
        />
      </CommonContainer>
    </BasicLayout>
  );
};

export default LocationManager;
